import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { EmptyState } from '@/shared/ui/EmptyState';
import { LoadingState, UnavailableState } from '@/shared/ui/States';

import { PICKER_DATASETS, useGroupName, useSuggestionCatalogue } from '../api/catalogue';
import { useSuggestionCounts } from '../api/rulegen';

/* The design's first two Suggestions steps: pick a source, then a table in it.
 *
 * **Two steps rather than one long dropdown**, because the question a steward arrives with is "which
 * database", and a flat select of every table in the tenant answered it only by scrolling. A source
 * nothing has been harvested from is not offered: suggestions are generated from a dataset's stored
 * schema, so it has nothing to suggest from.
 *
 * One catalogue read serves both steps, the same cached read the Datasets and Lineage screens make,
 * so a source's name and table count cannot disagree between them.
 */

/** The table row's status: unknown (`null`), never generated, nothing to review, or how many. */
function readinessOf(
  pending: ReadonlyMap<string, number> | null,
  datasetId: string,
): { readonly key: string; readonly count: number } | null {
  if (pending === null) return null;
  const count = pending.get(datasetId);
  if (count === undefined) return { key: 'suggestions.tables.notGenerated', count: 0 };
  if (count === 0) return { key: 'suggestions.tables.nothingToReview', count };
  return { key: 'suggestions.tables.toReview', count };
}

/** Step 1: the sources, as cards that say how many tables each holds. */
export function SuggestionSourcePicker({
  onPick,
}: {
  readonly onPick: (connectionId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { datasets, groups, types } = useSuggestionCatalogue();
  const nameOf = useGroupName();

  if (datasets.isPending) {
    return <LoadingState rows={4} label={t('suggestions.sources.loading')} />;
  }
  if (datasets.isError) {
    return (
      <UnavailableState
        title={t('suggestions.sources.unreadable')}
        guidance={datasets.error.message}
      />
    );
  }
  if (groups.length === 0) {
    return (
      <EmptyState
        title={t('suggestions.sources.empty')}
        guidance={t('suggestions.sources.emptyWhy')}
      />
    );
  }

  return (
    <div className="lq-sgp">
      <div className="lq-sgp__sources">
        {groups.map((group) => (
          <button
            type="button"
            key={group.connectionId}
            className="lq-sgp__source"
            onClick={() => {
              onPick(group.connectionId);
            }}
          >
            <span className="lq-sgp__sourceName">{nameOf(group)}</span>
            <span className="lq-sgp__sourceType">
              {types.get(group.connectionId) ?? ''}
            </span>
            <span className="lq-sgp__sourceCount">
              {t('suggestions.sources.count', { count: group.rows.length })}
            </span>
          </button>
        ))}
      </div>
      <p className="lq-sgp__note">
        {t('suggestions.sources.note', { limit: PICKER_DATASETS })}
      </p>
    </div>
  );
}

/** Step 2: one source's tables, filterable. */
export function SuggestionTablePicker({
  connectionId,
  onPick,
}: {
  readonly connectionId: string;
  readonly onPick: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { datasets, groups } = useSuggestionCatalogue();
  const nameOf = useGroupName();
  const counts = useSuggestionCounts();
  const [filter, setFilter] = useState('');

  if (datasets.isPending) {
    return <LoadingState rows={5} label={t('suggestions.sources.loading')} />;
  }
  if (datasets.isError) {
    return (
      <UnavailableState
        title={t('suggestions.sources.unreadable')}
        guidance={datasets.error.message}
      />
    );
  }

  const group = groups.find((each) => each.connectionId === connectionId);
  /* How many recommendations each generated table has left to review, from one read for the estate.
     A table absent from the answer was never generated; one present at zero was generated and has
     nothing left — every recommendation decided, or none justified — which is a different fact and
     must not read as "not generated yet". While the read is pending or has failed nothing is said
     either way, because a claim from a request that never answered has no grounds. */
  const pending = counts.isSuccess
    ? new Map(counts.data.counts.map((count) => [count.dataset_id, count.suggestions]))
    : null;
  const needle = filter.trim().toLowerCase();
  const rows = (group?.rows ?? []).filter(
    (row) =>
      needle === '' ||
      `${row.schema_name}.${row.object_name}`.toLowerCase().includes(needle),
  );

  return (
    <section className="lq-sgp__tables" aria-labelledby="suggestion-tables-heading">
      <div className="lq-sgp__tablesHead">
        <h2 id="suggestion-tables-heading" className="lq-sgp__tablesTitle">
          {group === undefined
            ? t('suggestions.tables.unknown')
            : t('suggestions.tables.in', { source: nameOf(group) })}
        </h2>
        <input
          type="search"
          className="lq-input lq-sgp__filter"
          aria-label={t('suggestions.tables.filter')}
          placeholder={t('suggestions.tables.filter')}
          value={filter}
          onChange={(event) => {
            setFilter(event.target.value);
          }}
        />
      </div>
      {rows.length === 0 ? (
        <p className="lq-sgp__none">
          {group === undefined || group.rows.length === 0
            ? t('suggestions.tables.none')
            : t('suggestions.tables.noMatch')}
        </p>
      ) : (
        <ul className="lq-sgp__rows">
          {rows.map((row) => (
            <li key={row.id} className="lq-sgp__rowItem">
              <button
                type="button"
                className="lq-sgp__row"
                onClick={() => {
                  onPick(row.id);
                }}
              >
                <span className="lq-sgp__rowName">
                  <DatasetObjectTypeIcon objectType={row.object_type} />
                  {`${row.schema_name}.${row.object_name}`}
                </span>
                <span className="lq-sgp__rowKind">
                  {t('suggestions.tables.version', { version: row.current_version })}
                </span>
                {/* Always present so the row's columns hold their place; empty while unknown. */}
                <span
                  className={
                    (pending?.get(row.id) ?? 0) > 0
                      ? 'lq-sgp__ready lq-sgp__ready--on'
                      : 'lq-sgp__ready'
                  }
                >
                  {(() => {
                    const readiness = readinessOf(pending, row.id);
                    return readiness === null
                      ? ''
                      : t(readiness.key, { count: readiness.count });
                  })()}
                </span>
                <span className="lq-sgp__arrow" aria-hidden="true">
                  →
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <p className="lq-sgp__note lq-sgp__note--inset">
        {t('suggestions.sources.note', { limit: PICKER_DATASETS })}
      </p>
    </section>
  );
}
